import React, { useEffect, useMemo, useState, memo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../data/store';
import { fetchProjectSubTypesThunk } from '../../data/thunks/projectConfigurationThunk';
import ScreenBusySpinner from '../common/ScreenBusySpinner';
import Button from '../common/buttons/Button';
import { ProjectSubType } from '../../data/models/projectConfiguration';
import { useNavigate } from 'react-router-dom';
import { Box, Card, Typography, Grid, IconButton } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { styled } from 'styled-components';
import ProjectSubTypesInfoModal from './ProjectSubTypesInfoModal';

// Move styled component outside and optimize
const CardContentContainer = styled.div`
	flex-grow: 1;
	padding: 16px 16px 8px 16px;
	display: flex;
	flex-direction: column;
	position: relative;
`;

// Pre-define card styles to avoid recalculation
const cardStyles = {
	height: '100%',
	display: 'flex',
	flexDirection: 'column',
	borderRadius: 2,
	border: '1px solid',
	borderColor: 'divider',
	'&:hover': {
		boxShadow: 2,
	},
	overflow: 'hidden',
} as const;

const headerBoxStyles = {
	backgroundColor: 'background.paper',
	color: 'white',
	p: 1,
	pl: 2,
	position: 'relative',
} as const;

const infoButtonStyles = {
	position: 'absolute',
	top: 8,
	right: 8,
	color: 'grey.400',
	'&:hover': {
		backgroundColor: 'rgba(255, 255, 255, 0.1)',
		color: 'white',
	},
} as const;

const viewButtonStyles = {
	textTransform: 'none',
	fontSize: '0.875rem',
	minWidth: 'auto',
	p: 0.5,
} as const;

export default function ProjectSubTypesScreen() {
	const dispatch = useDispatch<AppDispatch>();
	const { projectSubTypes, loading } = useSelector(
		(state: RootState) => state.projectConfigurationState
	);
	const [infoModalOpen, setInfoModalOpen] = useState(false);

	useEffect(() => {
		dispatch(fetchProjectSubTypesThunk());
	}, [dispatch]);

	return (
		<>
			<Box
				sx={{
					padding: 1,
					height: '100%',
					overflowY: 'auto',
				}}
			>
				<Typography
					variant="body2"
					color="text.secondary"
					sx={{ mb: 3, pl: 1 }}
				>
					View rules from project subtypes to enhance the efficiency
					of your project set-up
				</Typography>

				{loading ? (
					<ScreenBusySpinner />
				) : (
					<ProjectSubTypesGrid
						projectSubTypes={projectSubTypes}
						onInfoClick={() => setInfoModalOpen(true)}
					/>
				)}
			</Box>
			<ProjectSubTypesInfoModal
				open={infoModalOpen}
				onClose={() => setInfoModalOpen(false)}
			/>
		</>
	);
}

interface ProjectSubTypesGridProps {
	projectSubTypes: ProjectSubType[];
	onInfoClick: () => void;
}

// Memoize the grid component
const ProjectSubTypesGrid = memo(
	({ projectSubTypes, onInfoClick }: ProjectSubTypesGridProps) => {
		return (
			<Grid container spacing={3}>
				{projectSubTypes.map((subType) => (
					<Grid item xs={12} sm={6} md={4} lg={4} key={subType.id}>
						<ProjectSubTypeCard
							projectSubType={subType}
							onInfoClick={onInfoClick}
						/>
					</Grid>
				))}
			</Grid>
		);
	}
);

interface ProjectSubTypeCardProps {
	projectSubType: ProjectSubType;
	onInfoClick: () => void;
}

// Memoize the card component
const ProjectSubTypeCard = memo(
	({ projectSubType, onInfoClick }: ProjectSubTypeCardProps) => {
		const navigate = useNavigate();

		// Calculate total rules across all options
		const totalRules = useMemo(() => {
			return projectSubType.options.reduce((total, option) => {
				return total + (option.rules?.length || 0);
			}, 0);
		}, [projectSubType.options]);

		const handleViewRules = useCallback(() => {
			// Navigate to specific sub type rules page
			navigate(`/project-configuration/sub-types/${projectSubType.id}`);
		}, [navigate, projectSubType.id]);

		const handleInfoClick = useCallback(
			(e: React.MouseEvent) => {
				e.stopPropagation();
				onInfoClick();
			},
			[onInfoClick]
		);

		return (
			<Card sx={cardStyles}>
				{/* Header Section */}
				<Box sx={headerBoxStyles}>
					<Typography variant="h6">
						{projectSubType.display}
					</Typography>
					<IconButton
						size="small"
						sx={infoButtonStyles}
						onClick={handleInfoClick}
					>
						<InfoOutlinedIcon fontSize="small" />
					</IconButton>
				</Box>

				{/* Content Section */}
				<CardContentContainer>
					<Box sx={{ mb: 2, flexGrow: 1 }}>
						{totalRules === 0 ? (
							<Typography
								variant="body2"
								color="text.secondary"
								sx={{ fontStyle: 'italic' }}
							>
								No rules have been created
							</Typography>
						) : (
							<>
								<Typography
									variant="body2"
									color="text.secondary"
								>
									Created Rules
								</Typography>
								<Typography
									sx={{
										color: 'text.primary',
										mt: 0.5,
									}}
								>
									{totalRules}
								</Typography>
							</>
						)}
					</Box>

					{/* View Rules Button - positioned in bottom right */}
					<Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
						<Button
							onClick={handleViewRules}
							variant="text"
							color="primary"
							sz="small"
							sx={viewButtonStyles}
						>
							View Rules
						</Button>
					</Box>
				</CardContentContainer>
			</Card>
		);
	}
);
